import React, { useEffect, useState } from "react";
import type { AnyRoute } from "@tanstack/react-router";
import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useClerk } from "@clerk/tanstack-react-start";
import ImageLogoHorizontal from "~/img/logo/dcm-horizontal-1.png";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { Card } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { SignInForm } from "~/components/auth/sign-in-form";
import { WaveBackground } from "~/components/ui/wave-background";

export const Route = createFileRoute("/_auth/signin/")({
  validateSearch: (search: { [key: string]: unknown }) =>
    ({
      from: (search.from as string) || undefined,
      redirect: (search.redirect as string) || undefined,
    }) as { from?: string; redirect?: string },
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Sign In – Dream Coach Match",
      "Sign in to your Dream Coach Match account to access your coaching dashboard and manage your profile.",
    );

    return {
      ...metaConfig,
      links: [{ rel: "canonical", href: "https://dreamcoachmatch.com/signin" }],
    };
  },
  component: SigninPage,
});

function SigninPage() {
  const navigate = useNavigate();
  const clerk = useClerk();
  const search = useSearch({ from: "/_auth/signin/" });
  const fromAuthFailure = search?.from === "auth-failure";
  const redirectTo = (search?.redirect as string | undefined) ?? "/settings";

  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!clerk?.isSignedIn || fromAuthFailure) {
      return;
    }

    navigate({ to: redirectTo as AnyRoute["fullPath"] }).catch(() => {
      setAuthError("Navigation failed. Please try refreshing the page.");
    });
  }, [clerk?.isSignedIn, fromAuthFailure, navigate, redirectTo]);

  const hadValidSession = fromAuthFailure && clerk?.isSignedIn;

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-background">
      <div className="relative z-10 flex w-full max-w-[520px] flex-col gap-6 px-4">
        <Card className="space-y-6 p-6">
          <div className="space-y-2">
            <img
              src={ImageLogoHorizontal}
              alt="Dream Coach Match"
              className="w-[203px]"
            />
            <h2 className="text-xl font-semibold">Log in</h2>
            <p className="text-sm text-text-secondary">
              Access your account to continue discovering the right coach for
              you.
            </p>
          </div>

          {fromAuthFailure && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-left text-sm text-blue-700">
              <p className="font-medium">
                {hadValidSession ? "Session expired" : "Sign in required"}
              </p>
              <p className="mt-1">
                {hadValidSession
                  ? "Your session has expired. Please sign in again to continue."
                  : "Please sign in to access this page."}
                {redirectTo !== "/settings" && ` (${redirectTo})`}
              </p>
            </div>
          )}

          {authError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-left text-sm text-red-700">
              <p className="font-medium">Authentication error</p>
              <p className="mt-1">{authError}</p>
              <Button
                type="button"
                variant="destructive-ghost"
                size="sm"
                className="mt-3 h-auto px-3 py-1 text-xs"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.location.reload();
                  }
                }}
              >
                Refresh page
              </Button>
            </div>
          )}

          <SignInForm
            onSuccess={() =>
              navigate({ to: redirectTo as AnyRoute["fullPath"] })
            }
            emailLinkRedirectUrl={redirectTo}
          />

          <div className="text-right text-sm">
            <Link
              to="/forgot-password"
              className="text-text-primary/100 font-medium hover:underline"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="text-center text-sm text-text-secondary">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-text-primary/100 font-medium hover:underline"
            >
              Sign up
            </Link>
          </div>
        </Card>
      </div>
      <WaveBackground />
    </div>
  );
}
